/**
 * Filter utilities
 */
import type { FilterState, TimePreset } from '@/shared/types';
import type { EventsControllerGetStatsParams } from '@/generated/api/model';

/**
 * Map frontend TimePreset to backend preset format
 */
const PRESET_MAP: Record<string, 'today' | 'yesterday' | 'this_week' | 'this_month' | 'last_month' | 'this_quarter' | 'this_year'> = {
  today: 'today',
  yesterday: 'yesterday',
  this_week: 'this_week',
  this_month: 'this_month',
  last_month: 'last_month',
  this_quarter: 'this_quarter',
  this_year: 'this_year',
};

/**
 * Build API query params from filter state
 * Handles conversion for stats API endpoint
 */
export function buildStatsQuery(
  filters: FilterState,
  primaryBrand?: string,
  allowAllBrands: boolean = false,
): EventsControllerGetStatsParams | null {
  // Use first selected brand or primary brand
  const brand = primaryBrand || filters.brands[0];

  // If no brand and allowAllBrands is false, return null
  if (!brand && !allowAllBrands) {
    return null;
  }

  const query: EventsControllerGetStatsParams = {};

  // Add brand only if specified (omit for all brands aggregation)
  if (brand) {
    query.brand = brand;
  }

  // Time range
  if (filters.period === 'custom' && filters.dateFrom && filters.dateTo) {
    query.dateFrom = filters.dateFrom;
    query.dateTo = filters.dateTo;
  } else if (filters.period !== 'custom') {
    query.preset = PRESET_MAP[filters.period];
  }

  // Traffic source filter (sms, whatsapp, email, etc.)
  if (filters.sources.length === 1) {
    query.trafficSource = filters.sources[0];
  }

  // Affiliate filter
  if (filters.affiliateId) {
    query.affiliateId = filters.affiliateId;
  }

  // Tracking tag filters
  if (filters.trackingTag) {
    (query as any).trackingTag = filters.trackingTag;
  }
  if (filters.tagSender) {
    (query as any).tagSender = filters.tagSender;
  }
  if (filters.tagGeo) {
    (query as any).tagGeo = filters.tagGeo;
  }
  if (filters.tagSheetDate) {
    (query as any).tagSheetDate = filters.tagSheetDate;
  }

  // Test mode
  if (filters.testMode === 'exclude') {
    query.excludeTest = true;
  } else if (filters.testMode === 'include') {
    query.excludeTest = false;
  }
  // 'only' test mode would need backend support

  return query;
}

/**
 * Get display label for period
 */
export function getPeriodLabel(period: TimePreset | 'custom'): string {
  const labels: Record<string, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    this_week: 'This Week',
    this_month: 'This Month',
    last_month: 'Last Month',
    this_quarter: 'This Quarter',
    this_year: 'This Year',
    custom: 'Custom range',
  };
  return labels[period] || period;
}

/**
 * Validate custom date range
 */
export function isValidDateRange(dateFrom?: string, dateTo?: string): boolean {
  if (!dateFrom || !dateTo) return false;
  const from = new Date(dateFrom);
  const to = new Date(dateTo);
  return from <= to;
}

/**
 * Convert TimePreset to ISO date range for events API
 * Returns { fromDate, toDate } in ISO 8601 format
 */
export function getDateRangeFromPreset(preset: TimePreset): { fromDate: string; toDate: string } {
  const now = new Date();
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  let fromDate: Date;

  switch (preset) {
    case 'today': {
      fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      break;
    }
    case 'yesterday': {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      fromDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
      const endOfYesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
      return {
        fromDate: fromDate.toISOString(),
        toDate: endOfYesterday.toISOString(),
      };
    }
    case 'this_week': {
      // This week from Monday to now
      const dayOfWeek = now.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset, 0, 0, 0, 0);
      break;
    }
    case 'this_month': {
      // This month from 1st to now
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      break;
    }
    case 'last_month': {
      // Last month from 1st to last day
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      return {
        fromDate: lastMonth.toISOString(),
        toDate: endOfLastMonth.toISOString(),
      };
    }
    case 'this_quarter': {
      // This quarter from first day of quarter to now
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const quarterStartMonth = currentQuarter * 3;
      fromDate = new Date(now.getFullYear(), quarterStartMonth, 1, 0, 0, 0, 0);
      break;
    }
    case 'this_year': {
      // This year from January 1st to now
      fromDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      break;
    }
    default:
      fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  }

  return {
    fromDate: fromDate.toISOString(),
    toDate: endOfToday.toISOString(),
  };
}
